import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import StatsCard from '../components/StatsCard';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import { StatsCardSkeleton, DashboardRowSkeleton } from '../components/Skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  FolderKanban,
  Play,
  CheckCircle2,
  XCircle,
  PlusCircle,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function DashboardPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    try {
      const res = await runsAPI.list(20, 0);
      setRuns(res.data || []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const total = runs.length;
  const running = runs.filter((r) => r.status === 'running').length;
  const completed = runs.filter((r) => r.status === 'completed').length;
  const failed = runs.filter((r) => r.status === 'failed').length;
  const recent = runs.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Bienvenue */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Bonjour{user?.email ? `, ${user.email.split('@')[0]}` : ''}
          </h2>
          <p className="text-sm text-muted-foreground">
            Voici un résumé de vos projets
          </p>
        </div>
        <Button onClick={() => navigate('/projects/new')} className="glow-primary">
          <PlusCircle className="w-4 h-4 mr-2" />
          Nouveau projet
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
            <StatsCardSkeleton />
          </>
        ) : (
          <>
            <div className="animate-slide-up" style={{ animationDelay: '0ms' }}><StatsCard title="Total projets" value={total} icon={FolderKanban} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '50ms', animationFillMode: 'both' }}><StatsCard title="En cours" value={running} icon={Play} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '100ms', animationFillMode: 'both' }}><StatsCard title="Terminés" value={completed} icon={CheckCircle2} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '150ms', animationFillMode: 'both' }}><StatsCard title="Échoués" value={failed} icon={XCircle} /></div>
          </>
        )}
      </div>

      {/* Derniers projets */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">Projets récents</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/projects')} className="text-xs">
            Voir tout
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="divide-y divide-border">
              {[...Array(3)].map((_, i) => <DashboardRowSkeleton key={i} />)}
            </div>
          ) : recent.length === 0 ? (
            <EmptyState
              icon={FolderKanban}
              title="Aucun projet pour le moment"
              description="Créez votre premier projet pour commencer à générer du code avec l'IA."
              actionLabel="Créer votre premier projet"
              onAction={() => navigate('/projects/new')}
            />
          ) : (
            <div className="divide-y divide-border">
              {recent.map((run, idx) => (
                <button
                  key={run.id}
                  onClick={() => navigate(`/projects/${run.id}`)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-accent/50 transition-colors text-left animate-fade-in"
                  style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'both' }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {run.goal?.substring(0, 80) || 'Sans titre'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {run.stack} &middot;{' '}
                      {run.created_at
                        ? formatDistanceToNow(new Date(run.created_at), { addSuffix: true, locale: fr })
                        : ''}
                    </p>
                  </div>
                  <StatusBadge status={run.status} size="sm" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
